import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Video, Upload, Camera } from "lucide-react";
import { toast } from "sonner";

const MAX_S = 30;

/** Anexar vídeo (≤30s) ou substituir documento. Prévia antes do envio; progresso; novo arquivo fica Pendente de análise. */
export function AnexoDialog({ open, onClose, clienteId, substituir, onDone }) {
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState(null);
  const [dur, setDur] = useState(null);
  const [erro, setErro] = useState("");
  const [prog, setProg] = useState(0);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const camRef = useRef(null);
  const isVideo = !substituir || substituir.modalidade === "video";
  const reset = () => { setFile(null); setUrl(null); setDur(null); setErro(""); setProg(0); };
  const escolher = (f) => {
    reset();
    if (!f) return;
    const u = URL.createObjectURL(f); setFile(f); setUrl(u);
    if (f.type.startsWith("video/")) {
      const v = document.createElement("video"); v.preload = "metadata"; v.src = u;
      v.onloadedmetadata = () => { const d = v.duration; setDur(d); if (d > MAX_S + 0.05) setErro(`Vídeo com ${d.toFixed(1)}s excede 30 segundos. Selecione ou grave outro vídeo.`); };
      v.onerror = () => setErro("Não foi possível ler o vídeo. Use MP4/MOV/WEBM.");
    } else if (isVideo) setErro("Selecione um arquivo de vídeo.");
  };
  const enviar = async () => {
    if (!file || erro) return;
    setBusy(true); setProg(0);
    const fd = new FormData(); fd.append("file", file); if (isVideo) fd.append("duracao_informada", String(dur || 0));
    const rota = substituir ? `/documents/${substituir.id}/substituir` : `/clients/${clienteId}/documents/video`;
    try {
      await api.post(rota, fd, { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: (e) => setProg(Math.round((e.loaded / (e.total || 1)) * 100)) });
      toast.success(substituir ? "Documento substituído — aguardando nova análise" : "Vídeo enviado — pendente de análise"); onDone?.(); fechar();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha no envio. Tente novamente."); } finally { setBusy(false); }
  };
  const fechar = () => { reset(); onClose(); };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && fechar()}>
      <DialogContent className="max-w-lg" data-testid="anexo-dialog">
        <DialogHeader><DialogTitle>{substituir ? `Substituir: ${substituir.tipo}` : "Anexar vídeo"}</DialogTitle>
          <DialogDescription>{isVideo ? <b data-testid="anexo-duracao-max">Duração máxima: 30 segundos.</b> : "Selecione o novo arquivo (imagem ou PDF)."} {substituir && " O novo arquivo ficará Pendente de análise."}</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {isVideo && <Button variant="outline" onClick={() => camRef.current?.click()} className="gap-2 h-auto min-h-[2.75rem] whitespace-normal" data-testid="anexo-gravar"><Camera className="w-4 h-4" /> Gravar pela câmera</Button>}
          <Button variant="outline" onClick={() => inputRef.current?.click()} className={`gap-2 h-auto min-h-[2.75rem] whitespace-normal ${isVideo ? "" : "col-span-2"}`} data-testid="anexo-selecionar"><Upload className="w-4 h-4" /> {isVideo ? "Selecionar vídeo" : "Selecionar arquivo"}</Button>
          <input ref={camRef} type="file" accept="video/*" capture="environment" className="hidden" onChange={(e) => escolher(e.target.files?.[0])} data-testid="anexo-input-camera" />
          <input ref={inputRef} type="file" accept={isVideo ? "video/*" : "image/*,application/pdf"} className="hidden" onChange={(e) => escolher(e.target.files?.[0])} data-testid="anexo-input-arquivo" />
        </div>
        {url && (file.type.startsWith("video/") ? <video src={url} controls playsInline className="w-full rounded-lg border max-h-64 bg-black" data-testid="anexo-previa" /> : file.type.startsWith("image/") ? <img src={url} alt="prévia" className="w-full rounded-lg border max-h-64 object-contain" data-testid="anexo-previa" /> : <p className="text-sm" data-testid="anexo-previa">{file.name}</p>)}
        {dur != null && <p className="text-xs text-slate-600" data-testid="anexo-duracao">Duração: {dur.toFixed(1)}s</p>}
        {erro && <p className="text-sm text-red-600" data-testid="anexo-erro">{erro}</p>}
        {busy && <div className="h-2 rounded bg-slate-200 overflow-hidden" data-testid="anexo-progresso"><div className="h-full bg-gold transition-all" style={{ width: `${prog}%` }} /></div>}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={fechar} disabled={busy} data-testid="anexo-cancelar">Cancelar</Button>
          <Button onClick={enviar} disabled={!file || !!erro || busy} className="bg-onyx text-white gap-2" data-testid="anexo-confirmar">{busy ? <><Loader2 className="w-4 h-4 animate-spin" /> {prog}%</> : <><Video className="w-4 h-4" /> Confirmar envio</>}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
